import React, { useState } from 'react';
import { Users, Trophy, ArrowRight, ArrowDown, ArrowUp, Minus, Target, Package } from 'lucide-react';
import { UserProfile } from '../types';
import SEO from './SEO';

interface BagComparisonProps {
  userProfile: UserProfile | null;
}

// Pro club type (flexible string)
interface ProClub {
  type: string;
  brand: string;
  model: string;
  distance?: number;
  loft?: number;
}

// Tour Pro template bags
const TOUR_PRO_BAGS: Record<string, { name: string; handicap: string; description: string; clubs: ProClub[] }> = {
  'tiger-woods': {
    name: 'Tiger Woods',
    handicap: '+6',
    description: "15-time major champion's setup",
    clubs: [
      { type: 'Driver', brand: 'TaylorMade', model: 'Stealth 2 Plus', distance: 295, loft: 9 },
      { type: '3W', brand: 'TaylorMade', model: 'Stealth 2 Plus', distance: 265, loft: 15 },
      { type: '5W', brand: 'TaylorMade', model: 'Stealth 2 Plus', distance: 245, loft: 18 },
      { type: '3I', brand: 'TaylorMade', model: 'P7TW', distance: 230 },
      { type: '4I', brand: 'TaylorMade', model: 'P7TW', distance: 215 },
      { type: '5I', brand: 'TaylorMade', model: 'P7TW', distance: 200 },
      { type: '6I', brand: 'TaylorMade', model: 'P7TW', distance: 185 },
      { type: '7I', brand: 'TaylorMade', model: 'P7TW', distance: 170 },
      { type: '8I', brand: 'TaylorMade', model: 'P7TW', distance: 158 },
      { type: '9I', brand: 'TaylorMade', model: 'P7TW', distance: 145 },
      { type: 'PW', brand: 'TaylorMade', model: 'MG4', distance: 130, loft: 46 },
      { type: '56°', brand: 'TaylorMade', model: 'MG4', distance: 100, loft: 56 },
      { type: '60°', brand: 'TaylorMade', model: 'MG4', distance: 80, loft: 60 },
      { type: 'Putter', brand: 'Scotty Cameron', model: 'Newport 2 GSS' }
    ]
  },
  'rory-mcilroy': {
    name: 'Rory McIlroy',
    handicap: '+7',
    description: '4-time major champion, big hitter',
    clubs: [
      { type: 'Driver', brand: 'TaylorMade', model: 'Qi10 LS', distance: 310, loft: 8 },
      { type: '3W', brand: 'TaylorMade', model: 'Qi10', distance: 275, loft: 15 },
      { type: '5W', brand: 'TaylorMade', model: 'Qi10', distance: 255, loft: 18 },
      { type: '3I', brand: 'TaylorMade', model: 'P7MC', distance: 240 },
      { type: '4I', brand: 'TaylorMade', model: 'P7MC', distance: 225 },
      { type: '5I', brand: 'TaylorMade', model: 'P7MC', distance: 210 },
      { type: '6I', brand: 'TaylorMade', model: 'P7MC', distance: 195 },
      { type: '7I', brand: 'TaylorMade', model: 'P7MC', distance: 180 },
      { type: '8I', brand: 'TaylorMade', model: 'P7MC', distance: 168 },
      { type: '9I', brand: 'TaylorMade', model: 'P7MC', distance: 155 },
      { type: 'PW', brand: 'TaylorMade', model: 'MG4', distance: 140, loft: 46 },
      { type: '52°', brand: 'TaylorMade', model: 'MG4', distance: 115, loft: 52 },
      { type: '56°', brand: 'TaylorMade', model: 'MG4', distance: 100, loft: 56 },
      { type: '60°', brand: 'TaylorMade', model: 'MG4', distance: 80, loft: 60 },
      { type: 'Putter', brand: 'TaylorMade', model: 'Spider X' }
    ]
  },
  'scottie-scheffler': {
    name: 'Scottie Scheffler',
    handicap: '+7',
    description: 'World #1, 2024 Masters Champion',
    clubs: [
      { type: 'Driver', brand: 'Titleist', model: 'GT2', distance: 300, loft: 9 },
      { type: '3W', brand: 'Titleist', model: 'GT2', distance: 270, loft: 15 },
      { type: '5W', brand: 'Titleist', model: 'TSR2', distance: 250, loft: 18 },
      { type: '3I', brand: 'Titleist', model: 'T100', distance: 235 },
      { type: '4I', brand: 'Titleist', model: 'T100', distance: 220 },
      { type: '5I', brand: 'Titleist', model: 'T100', distance: 205 },
      { type: '6I', brand: 'Titleist', model: 'T100', distance: 190 },
      { type: '7I', brand: 'Titleist', model: 'T100', distance: 175 },
      { type: '8I', brand: 'Titleist', model: 'T100', distance: 163 },
      { type: '9I', brand: 'Titleist', model: 'T100', distance: 150 },
      { type: 'PW', brand: 'Titleist', model: 'Vokey SM10', distance: 135, loft: 46 },
      { type: '50°', brand: 'Titleist', model: 'Vokey SM10', distance: 120, loft: 50 },
      { type: '56°', brand: 'Titleist', model: 'Vokey SM10', distance: 100, loft: 56 },
      { type: '60°', brand: 'Titleist', model: 'Vokey SM10', distance: 75, loft: 60 },
      { type: 'Putter', brand: 'Scotty Cameron', model: 'Phantom X 5.5' }
    ]
  },
  'nelly-korda': {
    name: 'Nelly Korda',
    handicap: '+5',
    description: 'LPGA #1, Olympic Gold Medalist',
    clubs: [
      { type: 'Driver', brand: 'Titleist', model: 'TSR3', distance: 260, loft: 10.5 },
      { type: '3W', brand: 'Titleist', model: 'TSR2', distance: 230, loft: 15 },
      { type: '5W', brand: 'Titleist', model: 'TSR2', distance: 215, loft: 18 },
      { type: '4H', brand: 'Titleist', model: 'TSR2', distance: 195 },
      { type: '5I', brand: 'Titleist', model: 'T100', distance: 180 },
      { type: '6I', brand: 'Titleist', model: 'T100', distance: 168 },
      { type: '7I', brand: 'Titleist', model: 'T100', distance: 155 },
      { type: '8I', brand: 'Titleist', model: 'T100', distance: 143 },
      { type: '9I', brand: 'Titleist', model: 'T100', distance: 130 },
      { type: 'PW', brand: 'Titleist', model: 'Vokey SM9', distance: 118, loft: 46 },
      { type: '50°', brand: 'Titleist', model: 'Vokey SM9', distance: 105, loft: 50 },
      { type: '54°', brand: 'Titleist', model: 'Vokey SM9', distance: 90, loft: 54 },
      { type: '58°', brand: 'Titleist', model: 'Vokey SM9', distance: 70, loft: 58 },
      { type: 'Putter', brand: 'Scotty Cameron', model: 'Phantom X 11' }
    ]
  }
};

const BagComparison: React.FC<BagComparisonProps> = ({ userProfile }) => {
  const [selectedPro, setSelectedPro] = useState<string | null>(null);

  if (!userProfile || !userProfile.golfBag || userProfile.golfBag.length === 0) {
    return (
      <>
        <SEO 
          title="Bag Comparison"
          description="Compare your golf bag to tour pros and friends. See how your equipment stacks up."
          url="/compare"
        />
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Add Clubs First</h2>
            <p className="text-gray-600 mb-6">
              You need clubs in your bag to compare. Add some clubs to get started!
            </p>
          </div>
        </div>
      </>
    );
  }

  const userClubs = userProfile.golfBag;
  const proBag = selectedPro ? TOUR_PRO_BAGS[selectedPro] : null;

  // Calculate comparison metrics
  const getComparisonData = (proClubs: ProClub[]) => {
    const userDriver = userClubs.find(c => c.type === 'driver');
    const proDriver = proClubs.find(c => c.type === 'Driver');
    const user7Iron = userClubs.find(c => c.type === '7-iron');
    const pro7Iron = proClubs.find(c => c.type === '7I');

    return {
      driverDiff: (userDriver?.distance || 0) - (proDriver?.distance || 0),
      ironDiff: (user7Iron?.distance || 0) - (pro7Iron?.distance || 0),
      clubCountDiff: userClubs.length - proClubs.length,
      userDriverDist: userDriver?.distance || 0,
      proDriverDist: proDriver?.distance || 0,
      user7IronDist: user7Iron?.distance || 0,
      pro7IronDist: pro7Iron?.distance || 0
    };
  };

  return (
    <>
      <SEO 
        title="Bag Comparison"
        description="Compare your golf bag to tour pros. See how your equipment and distances stack up against the best players in the world."
        url="/compare"
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Bag Comparison</h1>
              <p className="text-indigo-100">Compare your setup to the pros</p>
            </div>
          </div>
        </div>

        {/* Pro Selection */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Select a Tour Pro to Compare
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(TOUR_PRO_BAGS).map(([key, pro]) => (
              <button
                key={key}
                onClick={() => setSelectedPro(key)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPro === key
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-semibold text-gray-900">{pro.name}</div>
                <div className="text-xs text-gray-500">{pro.handicap} handicap</div>
                <div className="text-xs text-gray-400 mt-1">{pro.clubs.length} clubs</div>
              </button>
            ))}
          </div>
        </div>

        {/* Comparison View */}
        {proBag && (
          <div className="space-y-6">
            {/* Stats Comparison */}
            {(() => {
              const data = getComparisonData(proBag.clubs);
              return (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm text-gray-500 mb-1">Driver Distance</div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{data.userDriverDist}</span>
                        <span className="text-gray-400">vs</span>
                        <span className="text-lg font-bold text-indigo-600">{data.proDriverDist}</span>
                      </div>
                      <div className={`text-sm font-medium mt-1 flex items-center justify-center gap-1 ${
                        data.driverDiff > 0 ? 'text-green-600' : data.driverDiff < 0 ? 'text-red-500' : 'text-gray-500'
                      }`}>
                        {data.driverDiff > 0 ? <ArrowUp className="w-4 h-4" /> : 
                         data.driverDiff < 0 ? <ArrowDown className="w-4 h-4" /> : 
                         <Minus className="w-4 h-4" />}
                        {Math.abs(data.driverDiff)} yds
                      </div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm text-gray-500 mb-1">7-Iron Distance</div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{data.user7IronDist}</span>
                        <span className="text-gray-400">vs</span>
                        <span className="text-lg font-bold text-indigo-600">{data.pro7IronDist}</span>
                      </div>
                      <div className={`text-sm font-medium mt-1 flex items-center justify-center gap-1 ${
                        data.ironDiff > 0 ? 'text-green-600' : data.ironDiff < 0 ? 'text-red-500' : 'text-gray-500'
                      }`}>
                        {data.ironDiff > 0 ? <ArrowUp className="w-4 h-4" /> : 
                         data.ironDiff < 0 ? <ArrowDown className="w-4 h-4" /> : 
                         <Minus className="w-4 h-4" />}
                        {Math.abs(data.ironDiff)} yds
                      </div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm text-gray-500 mb-1">Club Count</div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{userClubs.length}</span>
                        <span className="text-gray-400">vs</span>
                        <span className="text-lg font-bold text-indigo-600">{proBag.clubs.length}</span>
                      </div>
                      <div className={`text-sm font-medium mt-1 ${
                        data.clubCountDiff === 0 ? 'text-green-600' : 'text-amber-500'
                      }`}>
                        {data.clubCountDiff === 0 ? 'Same' : 
                         data.clubCountDiff > 0 ? `${data.clubCountDiff} more` : 
                         `${Math.abs(data.clubCountDiff)} fewer`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Side by Side Club List */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-2 divide-x">
                {/* Your Bag */}
                <div>
                  <div className="bg-gray-100 px-4 py-3 border-b">
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Your Bag
                    </div>
                    <div className="text-xs text-gray-500">{userClubs.length} clubs</div>
                  </div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {userClubs.map((club, idx) => (
                      <div key={idx} className="px-4 py-2 text-sm">
                        <div className="font-medium text-gray-900">{club.type}</div>
                        <div className="text-gray-500 text-xs">
                          {club.brand} {club.model}
                          {club.distance && ` • ${club.distance} yds`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pro Bag */}
                <div>
                  <div className="bg-indigo-50 px-4 py-3 border-b">
                    <div className="font-semibold text-indigo-900 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      {proBag.name}
                    </div>
                    <div className="text-xs text-indigo-600">{proBag.clubs.length} clubs</div>
                  </div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {proBag.clubs.map((club, idx) => (
                      <div key={idx} className="px-4 py-2 text-sm">
                        <div className="font-medium text-gray-900">{club.type}</div>
                        <div className="text-gray-500 text-xs">
                          {club.brand} {club.model}
                          {club.distance && ` • ${club.distance} yds`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Insights */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Key Insights
              </h3>
              <ul className="space-y-2 text-sm text-amber-800">
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Tour pros typically carry 14 clubs max and ensure consistent gapping throughout.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Distance differences are expected - focus on consistent gaps between your clubs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Pro wedge setups are optimized for their swing - yours should match your game.</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BagComparison;

